import React, { useState, Fragment } from "react";
import { DataGrid } from "@mui/x-data-grid";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";

import PencilOutline from "mdi-material-ui/PencilOutline";
import DeleteOutline from "mdi-material-ui/DeleteOutline";
import CustomChip from "@core/components/mui/chip";
import DialogModal from "@core/components/dialogmodal";
import QuickSearchToolbar from "views/components/QuickSearchToolbar";
import ImageModal from "@core/components/imagemodal";
import { isPermissionAllow } from "@core/utils/IsPermissionAllow";
import _ from "lodash";

const renderClient = (params) => {
  const { row } = params;

  if (row.image) {
    return <img alt="img" src={`${row.image}`} width="40px" height="40px" />;
  }
};

const statusObj = {
  0: { title: "Deactive", color: "error" },
  1: { title: "Active", color: "success" },
};

const BannerList = (props) => {
  const {
    handleDeleteModal,
    deleteOpen,
    handleOpen,
    bannerList,
    pageSize,
    searchText,
    pageNumber,
    setPageSize,
    setSearchText,
    setPageNumber,
    totalRecord,
    permission,
    role,
  } = props;

  const [deleteId, setDeleteId] = useState("");
  const [openImage, setOpenImage] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);

  const handleEdit = (id) => {
    handleOpen("edit", id);
  };

  const handleDelete = (id) => {
    handleDeleteModal();
    setDeleteId(id);
  };

  const handleImageModal = (url) => {
    setImageUrl(url);
    setOpenImage(true);
  };

  const handleClose = () => {
    setOpenImage(false);
  };

  const columns = [
    {
      flex: 1,
      minWidth: 150,
      field: "title",
      headerName: "Title",
    },
    {
      flex: 0.8,
      minWidth: 80,
      field: "image",
      headerName: "Image",
      renderCell: (params) => {
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
            }}
            onClick={() => handleImageModal(params.row.image)}
          >
            {renderClient(params)}
          </Box>
        );
      },
    },
    {
      flex: 0.8,
      minWidth: 120,
      field: "link",
      headerName: "Link",
      renderCell: ({ row }) => {
        if (!row.link) return;
        return (
          <Box
            onClick={() =>
              window.open(row.link, "_blank", "noopener,noreferrer")
            }
            sx={{ flexDirection: "column", display: "flex" }}
          >
            <Typography
              sx={{
                fontSize: "0.95rem !important",
                cursor: "pointer",
                color: "blue",
              }}
            >
              Link
            </Typography>
            <Box sx={{ height: 1.7, backgroundColor: "blue", width: "100%" }} />
          </Box>
        );
      },
    },
    {
      flex: 0.2,
      minWidth: 150,
      field: "priority",
      headerName: "Priority",
    },
    {
      flex: 0.2,
      minWidth: 150,
      field: "status",
      headerName: "Status",
      renderCell: ({ row }) => {
        const status = statusObj[row.status];

        return (
          <CustomChip
            size="small"
            skin="light"
            color={status.color}
            label={status.title}
            sx={{ "& .MuiChip-label": { textTransform: "capitalize" } }}
          />
        );
      },
    },
    {
      flex: 0.8,
      minWidth: 100,
      field: "action",
      headerName: "Actions",
      renderCell: ({ row }) => {
        return (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
              }}
            >
              {isPermissionAllow(permission.edit, role) && (
                <IconButton onClick={() => handleEdit(row.id)}>
                  <PencilOutline fontSize="medium" />
                </IconButton>
              )}
              {isPermissionAllow(permission.delete, role) && (
                <IconButton onClick={() => handleDelete(row.id)}>
                  <DeleteOutline fontSize="medium" />
                </IconButton>
              )}
            </Box>
          </Box>
        );
      },
    },
  ];

  const removeaction =
    !permission.edit && !permission.delete && role === "2"
      ? _.dropRight(columns)
      : columns;

  const handleSearch = (searchValue) => {
    setSearchText(searchValue);
  };

  return (
    <Fragment>
      <Card>
        <DataGrid
          hideFooterSelectedRowCount
          autoHeight
          localeText={{ noRowsLabel: "No results found" }}
          rowCount={Number(totalRecord)}
          page={pageNumber}
          columns={removeaction}
          rows={bannerList}
          pageSize={pageSize}
          rowsPerPageOptions={[10, 25, 50]}
          components={{ Toolbar: QuickSearchToolbar }}
          onPageChange={(newPageNumber) => setPageNumber(newPageNumber)}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          onSelectionModelChange={(rows) => setSelectedRows(rows)}
          pagination
          paginationMode="server"
          checkboxSelection={isPermissionAllow(permission.delete, role)}
          disableSelectionOnClick
          componentsProps={{
            toolbar: {
              value: searchText,
              clearSearch: () => handleSearch(""),
              onChange: (event) => handleSearch(event.target.value),
              handleOpen: () => handleOpen("add", ""),
              title: isPermissionAllow(permission.edit, role)
                ? "Add Banner"
                : "",
              selectedRows: selectedRows,
              onClickDelete: () => handleDeleteModal(),
              multiDelete: isPermissionAllow(permission.delete, role),
            },
          }}
          sx={{
            ".MuiTablePagination-displayedRows": {
              marginTop: "1rem",
            },
            ".MuiTablePagination-selectLabel": {
              marginTop: "1.1rem",
            },
            ".MuiInputBase-root": {
              marginTop: "0.2rem",
            },
          }}
        />
      </Card>

      {deleteOpen && (
        <DialogModal
          open={deleteOpen}
          handleClose={() => handleDeleteModal()}
          handleDelete={() =>
            handleDeleteModal(
              selectedRows && selectedRows.length === 0
                ? [deleteId]
                : selectedRows
            )
          }
        />
      )}

      {openImage && (
        <ImageModal
          open={openImage}
          handleClose={handleClose}
          imageUrl={imageUrl}
        />
      )}
    </Fragment>
  );
};

export default BannerList;
